import dayjs, { Dayjs } from "dayjs";
import { EMPTY_STR } from "@/constants/string";

export type TCompanyFilterFormExtended = {
  managementNumber: string;
  companyName: string[];
  companySpecificIndustryId: string[];
  prefecture: string[];
  city: string[];
  numberOfEmployees: string;
  contactPersonName: string[];
  jobCount: string[];
  registrationDateFrom: Dayjs | null;
  registrationDateTo: Dayjs | null;
  isShowCompanyArchive: number;
};

export type TCompanyDisplayColumnSetting = {
  id: string;
  label: string;
  visible: boolean;
  order: number;
};

export const FILTER_LABEL_MAX_LENGTH = 30;

export const defaultCompanyFilterValues: TCompanyFilterFormExtended = {
  managementNumber: "",
  companyName: [],
  companySpecificIndustryId: [],
  prefecture: [],
  city: [],
  numberOfEmployees: "",
  contactPersonName: [],
  jobCount: [],
  registrationDateFrom: null,
  registrationDateTo: null,
  isShowCompanyArchive: 0,
};

export const defaultCompanyDisplayColumns: TCompanyDisplayColumnSetting[] = [
  { id: "managementNumber", label: "企業管理番号", visible: true, order: 1 },
  { id: "companyName", label: "企業名", visible: true, order: 2 },
  { id: "companySpecificIndustryId", label: "分野", visible: true, order: 3 },
  { id: "prefecture", label: "所在地", visible: true, order: 4 },
  { id: "numberOfEmployees", label: "従業員数", visible: true, order: 5 },
  { id: "contactPersonName", label: "担当者", visible: true, order: 6 },
  { id: "jobCount", label: "求人数", visible: true, order: 7 },
  { id: "createdAt", label: "登録日", visible: true, order: 8 },
];

export const COMPANY_FILTER_FIELD_LABELS: { key: keyof TCompanyFilterFormExtended; label: string }[] = [
  { key: "managementNumber", label: "企業管理番号" },
  { key: "companyName", label: "企業名" },
  { key: "companySpecificIndustryId", label: "分野" },
  { key: "prefecture", label: "所在地（都道府県）" },
  { key: "city", label: "所在地（市区町村）" },
  { key: "numberOfEmployees", label: "従業員数" },
  { key: "contactPersonName", label: "担当者" },
  { key: "jobCount", label: "求人数" },
];

export const COMPANY_DATE_RANGE_FIELD = {
  fromKey: "registrationDateFrom" as const,
  toKey: "registrationDateTo" as const,
  label: "登録日",
};

// Normalize date fields from strings (JSON) to Dayjs objects
export const normalizeCompanyDateFields = (
  filters: Partial<TCompanyFilterFormExtended>,
): Partial<TCompanyFilterFormExtended> => {
  const result = { ...filters };
  if (result.registrationDateFrom && typeof result.registrationDateFrom === "string") {
    const day = dayjs(result.registrationDateFrom);
    result.registrationDateFrom = day.isValid() ? day : null;
  }
  if (result.registrationDateTo && typeof result.registrationDateTo === "string") {
    const day = dayjs(result.registrationDateTo);
    result.registrationDateTo = day.isValid() ? day : null;
  }
  return result;
};

// Serialize title + filters for dirty-checking comparison
export const serializeCompanyForComparison = (title: string, filters: TCompanyFilterFormExtended): string => {
  const normalized = { ...filters };
  if (normalized.registrationDateFrom) {
    normalized.registrationDateFrom = dayjs(normalized.registrationDateFrom).format("YYYY-MM-DD") as unknown as typeof normalized.registrationDateFrom;
  }
  if (normalized.registrationDateTo) {
    normalized.registrationDateTo = dayjs(normalized.registrationDateTo).format("YYYY-MM-DD") as unknown as typeof normalized.registrationDateTo;
  }
  return JSON.stringify({ title, filters: normalized });
};

/** Check if a filter value is empty (empty string, null, undefined, or empty array) */
export const isEmptyCompanyFilterValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

export const stripEmptyCompanyFilterFields = (filters: TCompanyFilterFormExtended): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyCompanyFilterValue(value)) continue;
    if (key === "isShowCompanyArchive" && value === 0) continue;
    result[key] = value;
  }
  return result;
};

/** Multi-select filter field keys */
export const COMPANY_MULTI_SELECT_FILTER_KEYS: (keyof TCompanyFilterFormExtended)[] = [
  "companyName",
  "companySpecificIndustryId",
  "prefecture",
  "city",
  "contactPersonName",
  "jobCount",
];

/** Normalize legacy scalar filter values to arrays for backward compatibility with saved conditions */
export const normalizeCompanyMultiSelectFields = (filters: Partial<Record<string, unknown>>): Partial<TCompanyFilterFormExtended> => {
  const result = { ...filters };
  for (const key of COMPANY_MULTI_SELECT_FILTER_KEYS) {
    const value = result[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) continue;
    if (value === "" || value === null || value === 0) {
      result[key] = [];
    } else {
      result[key] = [String(value)];
    }
  }
  return result as Partial<TCompanyFilterFormExtended>;
};

export const formatCompanyDateValue = (value: Dayjs | string | null): string => {
  if (!value) return EMPTY_STR.DATE;
  const day = dayjs(value);
  return day.isValid() ? day.format("YYYY/MM/DD") : EMPTY_STR.DATE;
};

export const COMPANY_LOCKED_COLUMN_IDS = ["companyName"];
